import { z } from "zod";

import {
    agentMessageSchema,
    agentOutputSchema,
    agentUsageSchema,
    serializedAgentErrorSchema,
    timestampSchema,
} from "./common.contract.ts";

export const agentTurnStatusSchema = z.enum([
    "queued",
    "running",
    "completed",
    "failed",
    "cancelled",
    "interrupted",
]);

export const agentTurnSchema = z
    .object({
        turnId: z.string().min(1),
        status: agentTurnStatusSchema,
        version: z.number().int().positive(),
        createdAt: timestampSchema,
        startedAt: timestampSchema.optional(),
        updatedAt: timestampSchema,
        startedCursor: z.number().int().positive().optional(),
        terminalCursor: z.number().int().positive().optional(),
        output: agentOutputSchema.optional(),
        messages: z.array(agentMessageSchema),
        usage: agentUsageSchema.optional(),
        error: serializedAgentErrorSchema.optional(),
    })
    .strict()
    .superRefine((turn, context) => {
        const terminal =
            turn.status === "completed" ||
            turn.status === "failed" ||
            turn.status === "cancelled" ||
            turn.status === "interrupted";
        if (terminal !== (turn.terminalCursor !== undefined)) {
            context.addIssue({
                code: "custom",
                path: ["terminalCursor"],
                message:
                    "Terminal cursor must be present only for terminal turns",
            });
        }
        if (
            turn.status !== "queued" &&
            turn.status !== "cancelled" &&
            turn.startedCursor === undefined
        ) {
            context.addIssue({
                code: "custom",
                path: ["startedCursor"],
                message: "Started cursor is required after a turn starts",
            });
        }
        if ((turn.status === "failed") !== (turn.error !== undefined)) {
            context.addIssue({
                code: "custom",
                path: ["error"],
                message: "Only failed turns carry an error",
            });
        }
    });

export const agentTurnResultSchema = agentTurnSchema.refine(
    (turn) =>
        turn.status === "completed" ||
        turn.status === "failed" ||
        turn.status === "cancelled" ||
        turn.status === "interrupted",
    { message: "Turn result must be terminal", path: ["status"] },
);

export type AgentTurn = z.infer<typeof agentTurnSchema>;
export type AgentTurnResult = z.infer<typeof agentTurnResultSchema>;
export type AgentTurnStatus = z.infer<typeof agentTurnStatusSchema>;
